import {
  ListItemText,
  Typography,
  TypographyProps,
  useMediaQuery,
} from "@mui/material"
import theme from "@skistarab/mui-theme"
import { useTranslation } from "react-i18next"

import type { AgeGroupWithPrice } from "@/components/ProductCard"
import { formatCurrency } from "@/utils/priceUtils"

type PriceDisplayProps = {
  price: AgeGroupWithPrice["price"]
  isSubscriptionPrice?: boolean
}

const secondaryLineProps: TypographyProps = {
  component: "span",
  display: "block",
  variant: "caption",
  color: "secondary",
}

const PriceDisplay = ({
  price,
  isSubscriptionPrice = false,
}: PriceDisplayProps) => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  if (!price) return null

  const suffix =
    price?.suffix + (isSubscriptionPrice ? t("subscriptionPriceSuffix") : "")

  let primaryText: string
  let secondaryText: string | undefined
  const primaryProps: TypographyProps = {
    variant: isMobile ? "body3" : "body2",
    fontWeight: "600",
  }

  if (price.discountedPrice) {
    primaryText = `${t("priceDisplay.discountedLabel")} ${formatCurrency(price.discountedPrice.price, suffix)}`
    secondaryText = `${t("priceDisplay.regularLabel")} ${formatCurrency(price.onlinePrice, suffix)}`
    primaryProps.color = "primary"
  } else if (price.sameDayPrice) {
    primaryText = `${formatCurrency(price.onlinePrice, suffix)}`
    secondaryText = `${t("priceDisplay.sameDayLabel")} ${formatCurrency(price.sameDayPrice, suffix)}`
  } else {
    primaryText = `${formatCurrency(price.onlinePrice, suffix)}`
  }

  const lowestPrice30DaysText =
    price.discountedPrice && price.lowestPrice30Days
      ? `${t("priceDisplay.lowestPrice30DaysLabel")} ${formatCurrency(price.lowestPrice30Days, suffix)}`
      : undefined

  const hasSecondary = !!secondaryText || !!lowestPrice30DaysText

  const secondaryNode = hasSecondary ? (
    <Typography component="span" display="block" sx={{ mt: { md: 1 } }}>
      {lowestPrice30DaysText && (
        <Typography {...secondaryLineProps}>{lowestPrice30DaysText}</Typography>
      )}
      {!lowestPrice30DaysText && secondaryText && (
        <Typography {...secondaryLineProps}>{secondaryText}</Typography>
      )}
    </Typography>
  ) : undefined

  return (
    <ListItemText
      primary={primaryText}
      secondary={secondaryNode}
      slotProps={{ primary: primaryProps }}
      sx={{ mt: 0, mb: 0 }}
    />
  )
}

export default PriceDisplay
